import React, {useState, useEffect} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useSelector} from 'react-redux'
import {useMediaQuery} from '@mui/material'
import BoxAtom from '../../@atoms/to_layout/box/box.atom'
import BrandGetComponent from '../brand_get/brand_get.component'
import CardLoginAtom from '../../@atoms/to_layout/card_login/card_login.atom'
import LoginFormComponent from '../login_form/login_form.component'
import SkeletonAtom from '../../@atoms/to_layout/skeleton/skeleton.atom'
import TypographyAtom from '../../@atoms/to_content/typography/typography.atom'
import {WithLoaderProps} from '../../@hoc/withloader/withloader.hoc'
import {RootState} from '../../store_redux'
import {
  AuthenticationTypeProps,
  AuthenticationTypeResponseProps,
} from '../../props_reusable/authentication'
import authenticationService from '../../services/authentication.service'
import utils from '../../utils'

interface LoginHeaderProps {
  error: boolean
  errorCode: string
}

const LoginHeader: React.FC<LoginHeaderProps> = function ({errorCode, error}) {
  const labels = useSelector((state: RootState) => state.labels)

  if (utils.isObjectEmpty(labels))
    return <SkeletonAtom ap_type="login_header_skeleton" />

  const getErrorMsg = () =>
    utils.getErrorMsg(labels.errors.authenticationType, errorCode)

  return (
    <>
      <BoxAtom className="dense-box">
        <TypographyAtom ahd_type="h7">{labels.login.title}</TypographyAtom>
      </BoxAtom>
      <BoxAtom className="dense-box">
        {!error ? (
          <TypographyAtom ahd_type="sData" textAlign="center">
            {labels.login.subtitle}
          </TypographyAtom>
        ) : (
          <TypographyAtom ahd_type="sData" ap_color="error" textAlign="center">
            {getErrorMsg()}
          </TypographyAtom>
        )}
      </BoxAtom>
    </>
  )
}

const LoginComponent: React.FC<WithLoaderProps> = function ({setLoading}) {
  const [authenticateType, setAuthenticateType] = useState<
    AuthenticationTypeProps | 'Error' | null
  >(null)
  const [errorCode, setErrorCode] = useState('')
  const [searchParams] = useSearchParams()
  const orderGUID = searchParams.get('orderGUID') || ''
  const matches = useMediaQuery('(max-width:480px)')

  const handleError = (error: Error) => {
    setAuthenticateType('Error')
    setErrorCode(error.message)
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (orderGUID) {
      let unmounted = false
      const source = utils.generateCancelTokenSource()
      const config = {
        cancelToken: source.token,
      }
      authenticationService
        .getAuthenticationType<AuthenticationTypeResponseProps>(
          orderGUID,
          config,
        )
        .then(res => !unmounted && setAuthenticateType(res.authenticationType))
        .catch(err => !unmounted && handleError(err))
        .finally(() => !unmounted && setLoading(false))

      return () => {
        unmounted = true
        source.cancel()
      }
    }
    utils.routeToErrorPage({message: '404'} as Error)

    // eslint-disable-next-line
  }, [orderGUID])

  if (!authenticateType) return null

  return (
    <CardLoginAtom>
      {matches && <BrandGetComponent ap_position="app_content_area" />}
      <LoginHeader error={authenticateType === 'Error'} errorCode={errorCode} />
      {authenticateType !== 'Error' && (
        <LoginFormComponent authenticateType={authenticateType} />
      )}
    </CardLoginAtom>
  )
}

export default LoginComponent
